import type { Appointment } from './appointmentManager';
import type { CustomerPickupBusinessPrefs } from '../types/customerPickupSettings';

const COLOR = {
  confirmed: '#34C759',
  inProgress: '#007AFF',
  readyPickup: '#34C759',
  completed: '#34C759',
  pending: '#FF9500',
  cancelled: '#FF3B30',
  declined: '#FF3B30',
  paid: '#6F42C1',
  default: '#8E8E93',
} as const;

/**
 * When the business uses pickup-style copy, map stored `completed` (and queue `completed`)
 * to the customer-facing label "Ready for pickup". Display-only; does not change stored data.
 */
export function getCustomerPickupAwareStatusLabel(
  apt: Appointment,
  pickupPrefs: CustomerPickupBusinessPrefs | undefined,
  defaultLabel: string
): string {
  if (!pickupPrefs?.pickupReadyDisplayEnabled) return defaultLabel;
  if (apt.status === 'completed' || apt.queueStage === 'completed') {
    return 'Ready for pickup';
  }
  return defaultLabel;
}

/**
 * Customer-facing badge on appointment cards (replaces raw `status` text when pickup prefs apply).
 */
export function getCustomerAppointmentBadge(
  apt: Appointment,
  pickupPrefs: CustomerPickupBusinessPrefs | undefined
): { text: string; backgroundColor: string } | null {
  const pickup = !!pickupPrefs?.pickupReadyDisplayEnabled;
  if (!pickup) return null;

  if (apt.status === 'cancelled' || apt.status === 'declined') return null;

  const stage = apt.queueStage;
  if (stage === 'in_service') {
    return { text: 'In progress', backgroundColor: COLOR.inProgress };
  }

  if (apt.status === 'completed' || stage === 'completed') {
    return { text: 'Ready for pickup', backgroundColor: COLOR.readyPickup };
  }

  return null;
}

/**
 * Line under "Queue status" on CustomerQueueStatusCard.
 */
export function getCustomerQueueStatusLine(
  apt: Appointment,
  pickupPrefs: CustomerPickupBusinessPrefs | undefined
): string {
  const pickup = !!pickupPrefs?.pickupReadyDisplayEnabled;
  const stage = apt.queueStage;

  if (pickup) {
    if (stage === 'in_service') return 'In progress';
    if (apt.status === 'completed' || stage === 'completed') return 'Ready for pickup';
  }

  switch (stage) {
    case 'checked_in':
      return 'Checked in';
    case 'in_service':
      return 'In service';
    case 'completed':
      return 'Completed';
    case 'no_show':
      return 'No-show';
    case 'booked':
    default:
      return 'Booked (not checked in yet)';
  }
}
